(() => {
  const roots = document.querySelectorAll("[data-dw-grading-checker]");
  if (!roots.length) return;

  const eur = new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });
  const usd = new Intl.NumberFormat("de-DE", { style: "currency", currency: "USD" });

  function parseNumber(value, fallback = 0) {
    const num = Number.parseFloat(String(value ?? "").replace(",", "."));
    return Number.isFinite(num) ? num : fallback;
  }

  function escapeHtml(value) {
    return String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  function debounce(fn, delay = 350) {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), delay);
    };
  }

  function token(value) {
    return String(value || "")
      .replace(/["'()]/g, "")
      .replace(/[^a-zA-Z0-9:.\- ]+/g, " ")
      .trim();
  }

  function normKeyPart(value) {
    return String(value || "")
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/&/g, " and ")
      .replace(/['’]/g, "")
      .replace(/[^a-zA-Z0-9]+/g, " ")
      .trim()
      .toLowerCase()
      .replace(/\s+/g, "-");
  }

  function numberKey(value) {
    return String(value || "").split("/")[0].replace(/^0+/, "").toLowerCase();
  }

  function cardIndexKeys(card) {
    return [
      card.id,
      `${normKeyPart(card.name)}|${normKeyPart(card.set?.name)}|${numberKey(card.number)}`,
    ].filter(Boolean);
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  function median(values) {
    const sorted = values.filter((value) => Number.isFinite(value) && value > 0).sort((a, b) => a - b);
    if (!sorted.length) return 0;
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  }

  function confidenceLabel(value) {
    if (value >= 0.82) return "hoch";
    if (value >= 0.58) return "mittel";
    return "niedrig";
  }

  function applyGermanAliases(value) {
    const aliases = [
      ["glurak", "charizard"],
      ["bisaflor", "venusaur"],
      ["turtok", "blastoise"],
      ["bisasam", "bulbasaur"],
      ["bisaknosp", "ivysaur"],
      ["glumanda", "charmander"],
      ["glutexo", "charmeleon"],
      ["schiggy", "squirtle"],
      ["schillok", "wartortle"],
      ["evoli", "eevee"],
      ["nachtara", "umbreon"],
      ["psiana", "espeon"],
      ["folipurba", "leafeon"],
      ["glaziola", "glaceon"],
      ["feelinara", "sylveon"],
      ["mewtu", "mewtwo"],
      ["relaxo", "snorlax"],
      ["garados", "gyarados"],
    ];
    let next = String(value || "").toLowerCase();
    aliases.forEach(([de, en]) => {
      next = next.replace(new RegExp(`\\b${de}\\b`, "gi"), en);
    });
    return next;
  }

  function buildQuery(raw) {
    const normalized = String(applyGermanAliases(raw) || "").toLowerCase();
    const numberMatch = normalized.match(/\b([a-z]{0,4}\d{1,4}[a-z]?)(?:\s*\/\s*\d{1,4})?\b/i);
    const number = numberMatch ? numberMatch[1].replace(/^0+/, "") : "";
    const nameSource = number
      ? normalized.replace(numberMatch?.[0] || "", " ").replace(/\b\d{1,4}\b/g, " ")
      : normalized;
    const name = token(nameSource).replace(/\s+/g, " ").trim();
    const parts = [];
    if (name) {
      const words = name.split(/\s+/).filter(Boolean).slice(0, 5);
      parts.push(...words.map((word) => `name:${word}*`));
    }
    if (number) parts.push(`number:${number}`);
    return parts.join(" ") || `name:${token(normalized)}*`;
  }

  function cardSearchText(card, grade = "") {
    return [
      card.name,
      card.set?.name,
      card.number ? `#${card.number}` : "",
      grade,
      "Pokemon card",
    ].filter(Boolean).join(" ");
  }

  function link(url, text) {
    return `<a href="${escapeHtml(url)}" target="_blank" rel="noopener">${escapeHtml(text)}</a>`;
  }

  function searchLinks(card) {
    const base = cardSearchText(card);
    const psa10 = cardSearchText(card, "PSA 10");
    const psa9 = cardSearchText(card, "PSA 9");
    return [
      card.cardmarket?.url ? { label: "Cardmarket", url: card.cardmarket.url } : null,
      card.tcgplayer?.url ? { label: "TCGplayer", url: card.tcgplayer.url } : null,
      { label: "eBay verkauft PSA 10", url: `https://www.ebay.de/sch/i.html?_nkw=${encodeURIComponent(psa10)}&LH_Sold=1&LH_Complete=1` },
      { label: "eBay verkauft PSA 9", url: `https://www.ebay.de/sch/i.html?_nkw=${encodeURIComponent(psa9)}&LH_Sold=1&LH_Complete=1` },
      { label: "PriceCharting", url: `https://www.pricecharting.com/search-products?type=prices&q=${encodeURIComponent(base)}` },
      { label: "Collectr App", url: "https://app.getcollectr.com/" },
    ].filter(Boolean);
  }

  async function fetchCards(query) {
    const params = new URLSearchParams({
      q: buildQuery(query),
      orderBy: "-set.releaseDate,number",
      pageSize: "18",
      select: "id,name,supertype,set,number,rarity,images,cardmarket,tcgplayer",
    });
    const response = await fetch(`https://api.pokemontcg.io/v2/cards?${params.toString()}`);
    if (!response.ok) throw new Error(`Pokemon TCG API ${response.status}`);
    const payload = await response.json();
    return (payload.data || []).filter((card) => card.supertype === "Pokémon");
  }

  function bestCardmarketPrice(card) {
    const prices = card.cardmarket?.prices || {};
    return (
      prices.trendPrice ||
      prices.avg7 ||
      prices.avg30 ||
      prices.averageSellPrice ||
      prices.lowPriceExPlus ||
      prices.lowPrice ||
      0
    );
  }

  function bestTcgplayerUsd(card) {
    const prices = card.tcgplayer?.prices || {};
    const buckets = ["holofoil", "reverseHolofoil", "normal", "1stEditionHolofoil"];
    for (const key of buckets) {
      const item = prices[key];
      if (!item) continue;
      const value = item.market || item.mid || item.low;
      if (value) return value;
    }
    return 0;
  }

  function normalizeSource(source, settings, fallbackType = "raw") {
    const currency = String(source.currency || source.currencyCode || "EUR").toUpperCase();
    const direct = parseNumber(source.valueEur ?? source.eur ?? source.priceEur, null);
    const nativeValue = parseNumber(source.value ?? source.price ?? source.amount, null);
    let valueEur = direct;
    if (!Number.isFinite(valueEur) && Number.isFinite(nativeValue)) {
      valueEur = currency === "USD" ? nativeValue * settings.usdToEur : nativeValue;
    }
    if (!Number.isFinite(valueEur) || valueEur <= 0) return null;

    return {
      source: source.source || source.marketplace || source.name || "Preisquelle",
      type: source.type || fallbackType,
      grade: String(source.grade || source.condition || "").toUpperCase().replace(/\s+/g, " "),
      valueEur,
      valueNative: nativeValue,
      currency,
      url: source.url || source.sourceUrl || "",
      confidence: clamp(parseNumber(source.confidence, fallbackType === "raw" ? 0.7 : 0.65), 0.15, 0.98),
      sampleSize: parseNumber(source.sampleSize ?? source.salesCount ?? source.count, 0),
      detail: source.detail || source.note || source.label || "",
      updatedAt: source.updatedAt || source.date || "",
    };
  }

  function localSources(card, settings) {
    const sources = [];
    const cm = bestCardmarketPrice(card);
    if (cm) {
      sources.push({
        source: "Cardmarket",
        type: "raw",
        valueEur: cm,
        currency: "EUR",
        url: card.cardmarket?.url || "",
        confidence: 0.86,
        detail: "Trend/Avg Rohpreis",
      });
    }
    const tcg = bestTcgplayerUsd(card);
    if (tcg) {
      sources.push({
        source: "TCGplayer",
        type: "raw",
        valueEur: tcg * settings.usdToEur,
        valueNative: tcg,
        currency: "USD",
        url: card.tcgplayer?.url || "",
        confidence: 0.62,
        detail: "US Market, FX umgerechnet",
      });
    }
    return sources;
  }

  function indexedSources(card, settings) {
    const index = window.DeckWaechterPsaPriceIndex?.entries || {};
    const hit = cardIndexKeys(card).map((item) => index[item]).find(Boolean);
    if (!hit) return [];
    const raw = (hit.rawSources || []).map((source) => normalizeSource(source, settings, "raw"));
    const graded = (hit.gradedSources || []).map((source) => normalizeSource(source, settings, "graded"));
    return mergeSourceLists(raw, graded).filter(Boolean);
  }

  function estimatePsa(card, rawEur) {
    const rarity = `${card.rarity || ""} ${card.name || ""}`;
    const highRarity = /special|illustration|secret|hyper|rainbow|alt|rare|promo/i.test(rarity);
    const chaseName = /charizard|glurak|pikachu|mewtwo|mew|umbreon|rayquaza|lugia|gengar|eevee|evoli|lillie|team rocket/i.test(rarity);
    let psa10Multiplier = rawEur < 5 ? 6 : rawEur < 20 ? 4.2 : rawEur < 75 ? 2.8 : 2.1;
    if (highRarity) psa10Multiplier += 0.8;
    if (chaseName) psa10Multiplier += 0.6;
    const psa10 = Math.max(rawEur * psa10Multiplier, rawEur + (rawEur < 20 ? 55 : 35));
    const psa9 = Math.max(rawEur * (rawEur < 20 ? 1.45 : 1.22), rawEur + 8);
    return [
      {
        source: "DeckWächter Schätzung",
        type: "graded",
        grade: "PSA 9",
        valueEur: Math.round(psa9),
        confidence: 0.35,
        detail: "Fallback ohne PSA-Preisfeed",
      },
      {
        source: "DeckWächter Schätzung",
        type: "graded",
        grade: "PSA 10",
        valueEur: Math.round(psa10),
        confidence: 0.35,
        detail: "Fallback ohne PSA-Preisfeed",
      },
    ];
  }

  function mergeSourceLists(...lists) {
    const seen = new Set();
    const merged = [];
    lists.flat().filter(Boolean).forEach((source) => {
      const key = `${source.source}|${source.type}|${source.grade}|${Math.round(source.valueEur * 100)}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(source);
    });
    return merged;
  }

  function weightedValue(sources) {
    const valid = sources.filter((source) => Number.isFinite(source.valueEur) && source.valueEur > 0);
    if (!valid.length) return 0;
    if (valid.length === 1) return valid[0].valueEur;
    const values = valid.map((source) => source.valueEur);
    const mid = median(values);
    const filtered = valid.filter((source) => {
      if (!mid) return true;
      const spread = Math.abs(source.valueEur - mid) / mid;
      return spread <= 0.55 || valid.length <= 2;
    });
    const totalWeight = filtered.reduce((sum, source) => sum + source.confidence, 0);
    if (!totalWeight) return mid;
    return filtered.reduce((sum, source) => sum + source.valueEur * source.confidence, 0) / totalWeight;
  }

  function splitSources(sources) {
    const raw = sources.filter((source) => source.type === "raw");
    const psa9 = sources.filter((source) => source.type === "graded" && /PSA\s*9|GEM\s*MINT\s*9|MINT\s*9/.test(source.grade));
    const psa10 = sources.filter((source) => source.type === "graded" && /PSA\s*10|GEM\s*MINT\s*10/.test(source.grade));
    return { raw, psa9, psa10 };
  }

  function net(sale, raw, settings) {
    if (!Number.isFinite(sale) || sale <= 0 || !Number.isFinite(raw) || raw <= 0) {
      return { profit: null, roi: null, breakeven: null, cost: raw + settings.gradingCost + settings.shippingBuffer, missing: true };
    }
    const fee = sale * settings.saleFee;
    const cost = raw + settings.gradingCost + settings.shippingBuffer;
    const profit = sale - fee - cost;
    const roi = cost > 0 ? (profit / cost) * 100 : 0;
    const breakeven = cost / Math.max(0.01, 1 - settings.saleFee);
    return { profit, roi, breakeven, cost };
  }

  function classFor(value) {
    if (value.missing) return "is-missing";
    if (value.profit >= 60 && value.roi >= 60) return "is-good";
    if (value.profit >= 15 && value.roi >= 20) return "is-watch";
    return "is-bad";
  }

  function verdict(psa9, psa10, conditionScore, sourceCount, values) {
    if (!values?.hasRealRaw) {
      return {
        title: "Rohpreis fehlt",
        text: "Ohne echten Rohpreis aus Collectr, Cardmarket, PriceCharting oder GRLD wird kein ROI berechnet.",
      };
    }
    if (!values?.hasRealPsa10 && psa10.missing) {
      return {
        title: "PSA10 Preis fehlt",
        text: "Für diese Karte liegt noch kein belastbarer PSA10-Wert vor. Bitte eBay/PriceCharting prüfen oder Preis manuell eintragen.",
      };
    }
    if (sourceCount < 3) {
      return {
        title: "Preisfeed fehlt noch",
        text: "Für echte PSA-Entscheidungen sollten eBay-Sold, PriceCharting oder ein eigener Collectr/Cardmarket-Feed angebunden sein.",
      };
    }
    if (conditionScore < 3) {
      return {
        title: "Nur nach genauer Zustandsprüfung",
        text: "Die Rechnung kann gut aussehen, aber bei Kanten, Ecken oder Oberfläche mit Problemen fällt der reale ROI schnell weg.",
      };
    }
    if (psa10.profit >= 80 && psa10.roi >= 70) {
      return {
        title: "Grading-Kandidat",
        text: "PSA 10 hätte rechnerisch Puffer. Vor dem Einsenden trotzdem Centering, Ecken, Kanten und Oberfläche mit Licht prüfen.",
      };
    }
    if (psa10.profit >= 25 && psa10.roi >= 25) {
      return {
        title: "Nur bei sehr sauberem Zustand",
        text: "Kann sich lohnen, wenn die Karte realistisch PSA 10 schaffen kann. PSA 9 sollte mindestens Break-even bleiben.",
      };
    }
    if (psa9.profit > 0) {
      return {
        title: "PSA 10 müsste wahrscheinlich sitzen",
        text: "PSA 9 ist knapp positiv, aber nach Zeit, Versand und Risiko nicht stark genug. Nur Top-Zustand priorisieren.",
      };
    }
    return {
      title: "Nicht priorisieren",
      text: "Nach Gebühren und Grading-Kosten ist zu wenig Puffer vorhanden. Lieber roh verkaufen oder behalten.",
    };
  }

  async function fetchMarketFeed(root, card) {
    const endpoint = root.dataset.psaEndpoint?.trim();
    if (!endpoint) return null;
    const url = new URL(endpoint, window.location.origin);
    url.searchParams.set("name", card.name || "");
    url.searchParams.set("set", card.set?.name || "");
    url.searchParams.set("number", card.number || "");
    url.searchParams.set("id", card.id || "");
    const response = await fetch(url.toString(), { credentials: "omit" });
    if (!response.ok) throw new Error(`market feed ${response.status}`);
    return response.json();
  }

  function normalizeMarketFeed(feed, settings) {
    if (!feed) return [];
    const raw = (feed.rawSources || feed.raw || []).map((source) => normalizeSource(source, settings, "raw"));
    const graded = (feed.gradedSources || feed.graded || feed.psaSources || []).map((source) => normalizeSource(source, settings, "graded"));
    const legacy = [];
    const psa9 = parseNumber(feed.psa9Eur, null);
    const psa10 = parseNumber(feed.psa10Eur, null);
    if (Number.isFinite(psa9)) {
      legacy.push(normalizeSource({
        source: feed.source || "Preisfeed",
        type: "graded",
        grade: "PSA 9",
        valueEur: psa9,
        url: feed.sourceUrl || "",
        confidence: 0.72,
        detail: "PSA 9 Feed",
      }, settings, "graded"));
    }
    if (Number.isFinite(psa10)) {
      legacy.push(normalizeSource({
        source: feed.source || "Preisfeed",
        type: "graded",
        grade: "PSA 10",
        valueEur: psa10,
        url: feed.sourceUrl || "",
        confidence: 0.72,
        detail: "PSA 10 Feed",
      }, settings, "graded"));
    }
    return mergeSourceLists(raw, graded, legacy).filter(Boolean);
  }

  function valueFromState(card, state) {
    const local = localSources(card, state.settings);
    const indexed = indexedSources(card, state.settings);
    const external = normalizeMarketFeed(state.marketFeed, state.settings);
    const sourceSplitWithoutEstimate = splitSources(mergeSourceLists(local, indexed, external));
    const rawValue = weightedValue(sourceSplitWithoutEstimate.raw);
    const estimate = rawValue > 0
      ? estimatePsa(card, rawValue).filter((source) => {
          if (/PSA\s*9/.test(source.grade) && sourceSplitWithoutEstimate.psa9.length) return false;
          if (/PSA\s*10/.test(source.grade) && sourceSplitWithoutEstimate.psa10.length) return false;
          return true;
        })
      : [];
    const sources = mergeSourceLists(local, indexed, external, estimate);
    const split = splitSources(sources);
    return {
      sources,
      rawSources: split.raw,
      psa9Sources: split.psa9,
      psa10Sources: split.psa10,
      raw: state.rawOverride ?? weightedValue(split.raw),
      psa9: state.psa9 ?? weightedValue(split.psa9),
      psa10: state.psa10 ?? weightedValue(split.psa10),
      externalCount: external.length,
      realSourceCount: sourceSplitWithoutEstimate.raw.length + sourceSplitWithoutEstimate.psa9.length + sourceSplitWithoutEstimate.psa10.length,
      hasRealRaw: sourceSplitWithoutEstimate.raw.length > 0,
      hasRealPsa9: sourceSplitWithoutEstimate.psa9.length > 0,
      hasRealPsa10: sourceSplitWithoutEstimate.psa10.length > 0,
    };
  }

  function renderResultButton(card, index, activeId, settings) {
    const raw = weightedValue(mergeSourceLists(localSources(card, settings), indexedSources(card, settings)).filter((source) => source.type === "raw"));
    const rawText = raw ? eur.format(raw) : "Preis offen";
    return `
      <button class="dw-grade-card ${activeId === card.id ? "is-active" : ""}" type="button" data-dw-grade-select="${index}">
        <img src="${escapeHtml(card.images?.small || card.images?.large || "")}" alt="${escapeHtml(card.name)}">
        <span>
          <strong>${escapeHtml(card.name)}</strong>
          <span>${escapeHtml(card.set?.name || "")} | ${escapeHtml(card.number || "")} | ${escapeHtml(card.rarity || "Rarity offen")}</span>
        </span>
        <b>${escapeHtml(rawText)}</b>
      </button>`;
  }

  function sourceRows(sources, emptyText) {
    if (!sources.length) {
      return `<p class="dw-grade__source-empty">${escapeHtml(emptyText)}</p>`;
    }
    return `
      <div class="dw-grade__source-table">
        ${sources.map((source) => `
          <div class="dw-grade-source-row">
            <span>
              <strong>${escapeHtml(source.source)}</strong>
              <small>${escapeHtml(source.detail || source.grade || source.type)}</small>
            </span>
            <b>${eur.format(source.valueEur)}</b>
            <em>${confidenceLabel(source.confidence)}${source.sampleSize ? ` | ${source.sampleSize} Verkäufe` : ""}</em>
          </div>
        `).join("")}
      </div>`;
  }

  function marketHtml(card, values) {
    const links = searchLinks(card);
    const graded = [...values.psa9Sources, ...values.psa10Sources]
      .sort((a, b) => a.grade.localeCompare(b.grade) || b.confidence - a.confidence);
    return `
      <div class="dw-grade__market">
        <div class="dw-grade__market-head">
          <strong>Preisquellen</strong>
          <span>${values.externalCount ? "Multi-Source Feed aktiv" : "Cardmarket/TCGplayer live, PSA Feed offen"}</span>
        </div>
        <h3>Rohpreise</h3>
        ${sourceRows(values.rawSources, "Noch kein Rohpreis gefunden.")}
        <h3>PSA 9/10 Preise</h3>
        ${sourceRows(graded, "Noch kein externer PSA-Preisfeed angebunden.")}
        <div class="dw-grade__checks">
          ${links.map((item) => link(item.url, item.label)).join("")}
        </div>
      </div>`;
  }

  function renderSummary(root, card, state) {
    const values = valueFromState(card, state);
    const conditionScore = state.conditionScore ?? 4;
    const psa9 = net(values.psa9, values.raw, state.settings);
    const psa10 = net(values.psa10, values.raw, state.settings);
    const v = verdict(psa9, psa10, conditionScore, values.realSourceCount, values);
    const summary = root.querySelector("[data-dw-grade-summary]");
    summary.innerHTML = `
      <div class="dw-grade-selected">
        <img src="${escapeHtml(card.images?.large || card.images?.small || "")}" alt="${escapeHtml(card.name)}">
        <div>
          <h2>${escapeHtml(card.name)}</h2>
          <span>${escapeHtml(card.set?.name || "")} | Nr. ${escapeHtml(card.number || "")} | ${escapeHtml(card.rarity || "Rarity offen")}</span>
          <div class="dw-grade__source">
            <span class="dw-grade-pill">${values.externalCount ? "Externer Feed" : "Basis-Feed"}</span>
            <span class="dw-grade-pill">${values.sources.length} Quellen</span>
          </div>
        </div>
      </div>

      ${marketHtml(card, values)}

      <div class="dw-grade__calc">
        <div class="dw-grade-row"><label>Rohpreis Mix</label><input data-dw-grade-raw type="number" min="0" step="0.01" value="${values.raw ? values.raw.toFixed(2) : ""}"></div>
        <div class="dw-grade-row"><label>PSA 9 Verkaufspreis</label><input data-dw-grade-psa9 type="number" min="0" step="1" value="${values.psa9 ? Math.round(values.psa9) : ""}"></div>
        <div class="dw-grade-row"><label>PSA 10 Verkaufspreis</label><input data-dw-grade-psa10 type="number" min="0" step="1" value="${values.psa10 ? Math.round(values.psa10) : ""}"></div>
      </div>

      <div class="dw-grade__condition" aria-label="Zustandseinschätzung">
        ${["Centering", "Ecken", "Kanten", "Oberfläche"].map((label, idx) => `
          <button type="button" class="${idx < conditionScore ? "is-active" : ""}" data-dw-condition="${idx + 1}">${label}</button>
        `).join("")}
      </div>

      <div class="dw-grade__outcomes" data-dw-outcomes>
        ${outcomeHtml("PSA 9", psa9)}
        ${outcomeHtml("PSA 10", psa10)}
      </div>

      <div class="dw-grade__verdict" data-dw-verdict>
        <strong>${escapeHtml(v.title)}</strong>
        <span>${escapeHtml(v.text)}${psa10.breakeven ? ` Break-even Verkaufspreis: ${eur.format(psa10.breakeven)}.` : ""}</span>
      </div>`;

    summary.querySelectorAll("input").forEach((input) => {
      input.addEventListener("input", () => {
        state.rawOverride = parseNumber(summary.querySelector("[data-dw-grade-raw]").value, 0);
        state.psa9 = parseNumber(summary.querySelector("[data-dw-grade-psa9]").value, 0);
        state.psa10 = parseNumber(summary.querySelector("[data-dw-grade-psa10]").value, 0);
        updateOutcome(summary, state, values);
      });
    });
    summary.querySelectorAll("[data-dw-condition]").forEach((button) => {
      button.addEventListener("click", () => {
        state.conditionScore = Number(button.dataset.dwCondition);
        renderSummary(root, card, state);
      });
    });
  }

  function outcomeHtml(label, result) {
    if (result.missing) {
      return `
      <article class="dw-grade-outcome is-missing">
        <strong>${label}</strong>
        <b>Preis fehlt</b>
        <small>Kein ROI ohne Rohpreis und Verkaufspreis.</small>
      </article>`;
    }
    return `
      <article class="dw-grade-outcome ${classFor(result)}">
        <strong>${label}</strong>
        <b>${eur.format(result.profit)}</b>
        <small>ROI ${Math.round(result.roi)} % | Kostenbasis ${eur.format(result.cost)}</small>
      </article>`;
  }

  function updateOutcome(summary, state, values) {
    const raw = state.rawOverride ?? 0;
    const psa9 = net(state.psa9 ?? 0, raw, state.settings);
    const psa10 = net(state.psa10 ?? 0, raw, state.settings);
    summary.querySelector("[data-dw-outcomes]").innerHTML = `${outcomeHtml("PSA 9", psa9)}${outcomeHtml("PSA 10", psa10)}`;
    const mergedValues = {
      ...values,
      hasRealRaw: values.hasRealRaw || raw > 0,
      hasRealPsa9: values.hasRealPsa9 || (state.psa9 ?? 0) > 0,
      hasRealPsa10: values.hasRealPsa10 || (state.psa10 ?? 0) > 0,
    };
    const v = verdict(psa9, psa10, state.conditionScore ?? 4, values.realSourceCount, mergedValues);
    summary.querySelector("[data-dw-verdict]").innerHTML = `<strong>${escapeHtml(v.title)}</strong><span>${escapeHtml(v.text)}${psa10.breakeven ? ` Break-even Verkaufspreis: ${eur.format(psa10.breakeven)}.` : ""}</span>`;
  }

  roots.forEach((root) => {
    const query = root.querySelector("[data-dw-grade-query]");
    const search = root.querySelector("[data-dw-grade-search]");
    const results = root.querySelector("[data-dw-grade-results]");
    const status = root.querySelector("[data-dw-grade-status]");
    const state = {
      cards: [],
      activeId: null,
      marketFeed: null,
      settings: {
        gradingCost: parseNumber(root.dataset.gradingCost, 35),
        shippingBuffer: parseNumber(root.dataset.shippingBuffer, 12),
        saleFee: parseNumber(root.dataset.saleFee, 13) / 100,
        usdToEur: parseNumber(root.dataset.usdEur, 0.86),
      },
      conditionScore: 4,
    };

    async function runSearch() {
      const value = query.value.trim();
      if (value.length < 3) {
        status.textContent = "Mindestens 3 Zeichen eingeben.";
        status.dataset.state = "";
        return;
      }
      status.textContent = "Live-Daten werden geladen ...";
      status.dataset.state = "";
      results.innerHTML = "";
      try {
        state.cards = await fetchCards(value);
        if (!state.cards.length) {
          status.textContent = "Keine Karte gefunden. Versuche Name + Kartennummer, z. B. Pikachu ex 238.";
          return;
        }
        status.textContent = `${state.cards.length} Treffer gefunden. Wähle die passende Karte.`;
        state.activeId = state.cards[0].id;
        results.innerHTML = state.cards.map((card, index) => renderResultButton(card, index, state.activeId, state.settings)).join("");
        selectCard(0);
      } catch (error) {
        status.textContent = "Live-Daten konnten nicht geladen werden. Bitte später erneut versuchen.";
        status.dataset.state = "error";
        console.warn(error);
      }
    }

    async function selectCard(index) {
      const card = state.cards[index];
      if (!card) return;
      state.activeId = card.id;
      state.marketFeed = null;
      state.rawOverride = null;
      state.psa9 = null;
      state.psa10 = null;
      results.innerHTML = state.cards.map((item, itemIndex) => renderResultButton(item, itemIndex, state.activeId, state.settings)).join("");
      renderSummary(root, card, state);
      try {
        state.marketFeed = await fetchMarketFeed(root, card);
        renderSummary(root, card, state);
      } catch (error) {
        console.warn(error);
      }
    }

    results.addEventListener("click", (event) => {
      const button = event.target.closest("[data-dw-grade-select]");
      if (!button) return;
      selectCard(Number(button.dataset.dwGradeSelect));
    });
    search.addEventListener("click", runSearch);
    query.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        event.preventDefault();
        runSearch();
      }
    });
    query.addEventListener("input", debounce(() => {
      if (query.value.trim().length >= 5) runSearch();
    }, 600));
  });
})();
